/**
 * TeamViewPage — 2026-06-07
 *
 * Dual-mode page:
 *   - ?user=<id>  → existing single-member detail view (kept as-is)
 *   - no query    → NEW live team roster — every visible member with
 *                   clock status, current task, and availability.
 *
 * Visibility:
 *   Owner / Admin → all members (admin excludes owner per visibility rules)
 *   Manager       → self + direct reports
 *   Member        → self only
 * The backend `/api/team-view/{ws}` endpoint applies the hierarchy gate;
 * this component just renders whatever it gets.
 *
 * Theme rule: theme-aware utilities only. No hardcoded hex.
 */
import { useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import {
  taskAPI, noteAPI, usersTableAPI, teamViewAPI,
} from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ArrowLeft, ClipboardList, Clock, CheckCircle2, AlertTriangle,
  StickyNote, Timer, Loader2, Users, PalmtreeIcon, RefreshCw, Coffee, Wifi, WifiOff, PlayCircle,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatTaskDate, formatTimeWithTz } from '../lib/dateFormat';
import { TaskDetailSheet } from '../components/TaskDetailSheet';

const PRIORITY_DOT = { high: 'bg-red-500/70', medium: 'bg-gold/70', low: 'bg-foreground/20' };
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', waiting: 'Waiting', review: 'Review', completed: 'Completed' };

// Live-roster status pill styling — theme-aware.
const ROSTER_STATUS = {
  active:      { label: 'Working on task', dot: 'bg-emerald-500',          chip: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  clocked_in:  { label: 'Clocked in',      dot: 'bg-gold',                 chip: 'bg-gold/10 text-gold border-gold/30' },
  on_break:    { label: 'On break',        dot: 'bg-amber-500',            chip: 'bg-amber-500/10 text-amber-500 border-amber-500/30' },
  online:      { label: 'Online',          dot: 'bg-sky-500',              chip: 'bg-sky-500/10 text-sky-500 border-sky-500/20' },
  on_leave:    { label: 'On approved leave', dot: 'bg-purple-500',         chip: 'bg-purple-500/10 text-purple-500 border-purple-500/30' },
  away:        { label: 'Clocked out',     dot: 'bg-muted-foreground/40',  chip: 'bg-muted/50 text-muted-foreground border-border/40' },
};

const ROLE_FILTERS = [
  { value: 'all', label: 'All roles' },
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'team', label: 'Team' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'working', label: 'Working' },
  { value: 'clocked_in', label: 'Clocked in' },
  { value: 'on_break', label: 'On break' },
  { value: 'on_leave', label: 'On leave' },
  { value: 'away', label: 'Away' },
];

export default function TeamViewPage() {
  const { currentWorkspace } = useWorkspace();
  const [searchParams] = useSearchParams();
  const viewUserId = searchParams.get('user');
  if (viewUserId) return <SingleMemberView userId={viewUserId} />;
  return <RosterView />;
}

// ── ROSTER VIEW — full team live overview ──────────────────────────────
function RosterView() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roster, setRoster] = useState({ members: [], viewer_role: '', visibility: 'all' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const fetchRoster = (silent = false) => {
    if (!currentWorkspace?.id) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    teamViewAPI
      .roster(currentWorkspace.id)
      .then((r) => setRoster(r.data || { members: [] }))
      .catch(() => {})
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    fetchRoster(false);
    // light auto-refresh — every 45s
    // 2026-06-16 — Tighter poll (was 45s). Spec requires the dashboard /
    // Team Pulse to reflect a task switch within 30 s. We poll at 30 s and
    // the backend resolver short-circuits on presence/timer so the queries
    // are cheap.
    const t = setInterval(() => fetchRoster(true), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (roster.members || []).filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (statusFilter !== 'all') {
        if (statusFilter === 'working' && m.status !== 'active') return false;
        if (statusFilter !== 'working' && m.status !== statusFilter) return false;
      }
      if (q && !(`${m.name} ${m.email}`).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [roster, query, roleFilter, statusFilter]);

  const summary = useMemo(() => {
    const ms = roster.members || [];
    return {
      total: ms.length,
      working: ms.filter((m) => m.status === 'active').length,
      clocked_in: ms.filter((m) => m.is_clocked_in).length,
      on_leave: ms.filter((m) => m.on_leave_today).length,
    };
  }, [roster]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6" data-testid="team-view-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-gold" /> Team View
          </h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Live overview of your team — clock status, what they’re working on, and availability.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchRoster(true)}
          disabled={loading || refreshing}
          data-testid="team-view-refresh"
        >
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground"><Users className="h-3.5 w-3.5" /> Members</div>
            <div className="text-2xl font-semibold mt-1 text-foreground" data-testid="team-view-count-total">{summary.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground"><PlayCircle className="h-3.5 w-3.5" /> Working now</div>
            <div className="text-2xl font-semibold mt-1 text-gold" data-testid="team-view-count-working">{summary.working}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Clocked in</div>
            <div className="text-2xl font-semibold mt-1 text-foreground" data-testid="team-view-count-clockedin">{summary.clocked_in}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground"><Coffee className="h-3.5 w-3.5" /> On approved leave</div>
            <div className="text-2xl font-semibold mt-1 text-foreground" data-testid="team-view-count-leave">{summary.on_leave}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="team-view-filters">
        <Input
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-xs h-9"
          data-testid="team-view-search"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="team-view-role-filter"
          className="h-9 rounded-md bg-background border border-border px-3 text-[13px]"
        >
          {ROLE_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="team-view-status-filter"
          className="h-9 rounded-md bg-background border border-border px-3 text-[13px]"
        >
          {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Roster */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading team…
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-[13px] text-muted-foreground" data-testid="team-view-empty">
            No members match your filters.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((m) => {
            const sty = ROSTER_STATUS[m.status] || ROSTER_STATUS.away;
            const initials = (m.name || m.email || '?').split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();
            return (
              <Card
                key={m.user_id}
                className="hover:border-gold/30 transition-colors cursor-pointer"
                data-testid={`team-view-member-${m.user_id}`}
                onClick={() => navigate(`/tasks/team-view?user=${m.user_id}`)}
              >
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 relative">
                      <AvatarFallback className="text-[11px] bg-muted text-foreground">{initials}</AvatarFallback>
                      <span className={`absolute -bottom-0 -right-0 h-2.5 w-2.5 rounded-full ring-2 ring-card ${sty.dot}`} />
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground text-[14px] truncate">{m.name || m.email}</p>
                        <Badge variant="outline" className="text-[9px] capitalize border-border/40">{m.role}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                    </div>
                  </div>

                  {/* Status pill */}
                  <div>
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${sty.chip}`}
                      data-testid={`team-view-status-${m.user_id}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${sty.dot}`} />
                      {sty.label}
                      {m.on_break && m.status !== 'on_break' && <span className="opacity-70">• on break</span>}
                    </span>
                  </div>

                  {/* Current task */}
                  {m.active_task ? (
                    <div
                      className="rounded-md border border-border/40 bg-muted/30 px-2.5 py-2"
                      onClick={(e) => { e.stopPropagation(); if (m.active_task_id) setSelectedTaskId(m.active_task_id); }}
                      role="button"
                      data-testid={`team-view-task-${m.user_id}`}
                    >
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                        <Timer className="h-3 w-3" /> {m.active_task_is_timed ? 'Running timer' : 'In progress'}
                      </p>
                      <p className="text-[13px] text-foreground line-clamp-1 mt-0.5">
                        {m.active_task_client ? <span className="text-gold">{m.active_task_client}: </span> : null}
                        {m.active_task}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-border/30 px-2.5 py-2">
                      <p className="text-[11px] text-muted-foreground" data-testid={`team-view-no-task-${m.user_id}`}>
                        {m.on_leave_today
                          ? `On approved leave${m.leave_end_date ? ` until ${m.leave_end_date}` : ''}${m.admin_managed_leave ? ' (admin)' : ''}`
                          : m.on_break
                          ? 'On break'
                          : m.is_clocked_in
                          ? 'Not on a task'
                          : 'Offline'}
                      </p>
                    </div>
                  )}

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    {m.is_clocked_in ? (
                      <span className="inline-flex items-center gap-1"><Wifi className="h-3 w-3" /> since {fmtClockIn(m.clock_in_time)}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><WifiOff className="h-3 w-3" /> Clocked out</span>
                    )}
                    {m.timezone && <span>{formatTimeWithTz(m.timezone)}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <TaskDetailSheet
        taskId={selectedTaskId}
        open={!!selectedTaskId}
        onOpenChange={(o) => { if (!o) setSelectedTaskId(null); }}
      />
    </div>
  );
}

function fmtClockIn(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

// ── SINGLE-MEMBER VIEW — preserved original behaviour ──────────────────
function SingleMemberView({ userId }) {
  const { currentWorkspace } = useWorkspace();
  const [member, setMember] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentWorkspace?.id || !userId) return;
    setLoading(true);
    Promise.all([
      usersTableAPI.list(currentWorkspace.id),
      taskAPI.list(currentWorkspace.id, { assigned_to: userId }),
      noteAPI.list(currentWorkspace.id).catch(() => ({ data: [] })),
    ]).then(([uRes, tRes, nRes]) => {
      const m = uRes.data.find(u => u.user_id === userId);
      setMember(m);
      setTasks(tRes.data);
      setNotes(nRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [currentWorkspace?.id, userId]);

  if (loading) return <div className="p-8"><div className="h-8 w-48 bg-secondary/30 rounded animate-pulse" /></div>;
  if (!member) return <div className="p-8 text-muted-foreground" data-testid="team-view-member-missing">Member not found</div>;

  const open = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const completed = tasks.filter(t => t.status === 'completed');
  const overdue = tasks.filter(t => t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && t.status !== 'completed');
  const totalHours = tasks.reduce((s, t) => s + (t.hours_logged || 0), 0);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6" data-testid="team-view-single">
      <div>
        <button onClick={() => navigate('/tasks/team-view')} className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground mb-3" data-testid="team-view-back-roster">
          <ArrowLeft className="h-3 w-3" /> Back to Team View
        </button>
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
            <span className="text-lg font-semibold">{(member.name || '?')[0].toUpperCase()}</span>
          </div>
          <div>
            <h1 className="text-xl font-display font-bold">{member.name}</h1>
            <div className="flex items-center gap-3 text-[12px] text-muted-foreground mt-0.5">
              <span>{member.email}</span>
              <Badge variant="outline" className="text-[10px] capitalize">{member.role}</Badge>
              {member.timezone && <span>{formatTimeWithTz(member.timezone)}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="card-hover-gold"><CardContent className="p-4">
          <ClipboardList className="h-4 w-4 text-muted-foreground/40 mb-2" />
          <p className="text-xl font-display font-bold">{open.length}</p>
          <p className="text-[11px] text-muted-foreground">Open Tasks</p>
        </CardContent></Card>
        <Card className="card-hover-gold"><CardContent className="p-4">
          <CheckCircle2 className="h-4 w-4 text-muted-foreground/40 mb-2" />
          <p className="text-xl font-display font-bold">{completed.length}</p>
          <p className="text-[11px] text-muted-foreground">Completed</p>
        </CardContent></Card>
        <Card className="card-hover-gold"><CardContent className="p-4">
          <AlertTriangle className="h-4 w-4 text-red-500/40 mb-2" />
          <p className="text-xl font-display font-bold">{overdue.length}</p>
          <p className="text-[11px] text-muted-foreground">Overdue</p>
        </CardContent></Card>
        <Card className="card-hover-gold"><CardContent className="p-4">
          <Timer className="h-4 w-4 text-muted-foreground/40 mb-2" />
          <p className="text-xl font-display font-bold">{totalHours.toFixed(1)}h</p>
          <p className="text-[11px] text-muted-foreground">Hours Logged</p>
        </CardContent></Card>
      </div>

      {tasks.length > 0 && (
        <div>
          <div className="flex items-center justify-between text-[12px] mb-1.5">
            <span className="text-muted-foreground">Completion Rate</span>
            <span className="font-medium">{Math.round((completed.length / tasks.length) * 100)}%</span>
          </div>
          <Progress value={Math.round((completed.length / tasks.length) * 100)} className="h-1.5" />
        </div>
      )}

      <Separator />

      <div>
        <h2 className="text-[13px] font-semibold mb-3">Open Tasks ({open.length})</h2>
        <div className="space-y-0.5">
          {open.map(t => (
            <button key={t.id} onClick={() => setSelectedTaskId(t.id)} className="flex items-center gap-3 w-full py-2 px-3 rounded-md task-row-hover text-left">
              <span className={`h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[t.priority]}`} />
              <span className="text-[13px] truncate flex-1">{t.title}</span>
              <Badge variant="outline" className="text-[9px]">{STATUS_LABELS[t.status] || t.status}</Badge>
              {t.due_date && <span className="text-[11px] text-muted-foreground">{formatTaskDate(t.due_date)}</span>}
            </button>
          ))}
          {open.length === 0 && <p className="text-[12px] text-muted-foreground/40 py-4">No open tasks</p>}
        </div>
      </div>

      {notes.length > 0 && (
        <div>
          <h2 className="text-[13px] font-semibold mb-3 flex items-center gap-2"><StickyNote className="h-3.5 w-3.5" /> Notes ({notes.length})</h2>
          <div className="space-y-1">
            {notes.slice(0, 5).map(n => (
              <div key={n.id} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-foreground/[0.02]">
                <StickyNote className="h-3 w-3 text-muted-foreground/30 shrink-0" />
                <span className="text-[12px] truncate flex-1">{n.title}</span>
                <span className="text-[10px] text-muted-foreground/40">{formatTaskDate(n.updated_at?.slice(0, 10))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <TaskDetailSheet taskId={selectedTaskId} open={!!selectedTaskId} onOpenChange={o => { if (!o) setSelectedTaskId(null); }} />
    </div>
  );
}
